/* eslint-disable @typescript-eslint/no-explicit-any */
import { Search } from '@mui/icons-material';
import {
  Box, Button, Chip, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent,
  Stack, SvgIcon, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TextField,
  Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash01 } from '../../../assets';
import { promotionsClient, PromotionDto } from '../../../app/services/api/promotions';
import { PromotionStatusType } from '../../../app/services/api/promotions/promotionTypes';
import { client } from '../../../app/services/api/orderManagementClient';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { Pill } from '../../../components/shared/Pill';
import { UserContext } from '../../../components/shared/useUser';
import CustomPagination from '../../../components/forms/Pagination';
import { TableLoading } from '../../../components/forms/TableLoading';
import { Section } from '../../../components/ui/Section';

interface InstanceOption { id: number; name: string; }
interface MarketOption { id: number; name: string; companyId: number; }

const PROMOTIONS_PAGE_SIZE = 10;
const PROMOTIONS_HEADER_HEIGHT = 45;
const PROMOTIONS_ROW_HEIGHT = 56;

type StatusFilter = '' | PromotionStatusType;

const ManagePromotions = () => {
  const { t } = useTranslation('promotions');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { hasPermissionTo, selectedStore, selectedFranchise } = useContext(UserContext);

  const [promotions, setPromotions] = useState<PromotionDto[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<StatusFilter>('');
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedPromotions, setHasLoadedPromotions] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<PromotionDto | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // Instance / Market
  const [instances, setInstances] = useState<InstanceOption[]>([]);
  const [markets, setMarkets] = useState<MarketOption[]>([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [selectedMarket, setSelectedMarket] = useState('');
  const [loggedInCompanyId, setLoggedInCompanyId] = useState<number | null>(null);

  const canViewPromotions = hasPermissionTo(['PromotionRead']);
  const canWritePromotions = hasPermissionTo(['PromotionWrite']);
  const canDeletePromotions = hasPermissionTo(['PromotionDelete']);

  const filteredPromotions = useMemo(() => {
    let list = promotions;
    if (selectedStatus) list = list.filter((p) => p.status === selectedStatus);
    return list;
  }, [promotions, selectedStatus]);

  const maxPages = Math.max(Math.ceil(filteredPromotions.length / PROMOTIONS_PAGE_SIZE), 1);

  const visiblePromotions = useMemo(
    () => filteredPromotions.slice(currentPage * PROMOTIONS_PAGE_SIZE, (currentPage + 1) * PROMOTIONS_PAGE_SIZE),
    [currentPage, filteredPromotions],
  );

  useEffect(() => {
    const storeNumber = selectedStore?.storeNumber?.trim();
    if (!storeNumber) { setLoggedInCompanyId(null); return; }
    let isActive = true;
    client.getCompanyIdByStoreNumber(storeNumber)
      .then((result) => { if (isActive && result.success) setLoggedInCompanyId(result.data ?? null); })
      .catch(() => {});
    return () => { isActive = false; };
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    let isActive = true;
    client.getInstances({ name: '', sort: 'name', direction: 'asc', pageNumber: 0, pageSize: 200 })
      .then((result) => {
        if (isActive) setInstances((result.data?.items ?? []).map((i) => ({ id: i.id, name: i.name })));
      })
      .catch(() => {});
    return () => { isActive = false; };
  }, []);

  useEffect(() => {
    if (selectedInstance || instances.length === 0 || loggedInCompanyId === null) return;
    if (instances.some((i) => i.id === loggedInCompanyId)) {
      setSelectedInstance(loggedInCompanyId.toString());
    }
  }, [instances, loggedInCompanyId, selectedInstance]);

  useEffect(() => {
    if (!selectedInstance) { setMarkets([]); return; }
    let isActive = true;
    client.getMarkets({
      companyId: selectedInstance, name: '', sort: 'name', direction: 'asc', pageNumber: 0, pageSize: 500,
    })
      .then((result) => {
        if (isActive) setMarkets((result.data?.items ?? []).map((m) => ({ id: m.customerTypeId, name: m.name, companyId: m.companyId })));
      })
      .catch(() => {});
    return () => { isActive = false; };
  }, [selectedInstance]);

  useEffect(() => {
    if (!selectedInstance || selectedMarket || markets.length === 0) return;
    const franchiseName = selectedFranchise?.franchiseName?.trim().toLowerCase();
    if (!franchiseName) return;
    const match = markets.find((m) => m.name.trim().toLowerCase() === franchiseName);
    if (match) setSelectedMarket(match.id.toString());
  }, [markets, selectedFranchise?.franchiseName, selectedInstance, selectedMarket]);

  const loadPromotions = async () => {
    if (!canViewPromotions || !selectedInstance || !selectedMarket) return;

    setIsLoading(true);
    try {
      const data = await promotionsClient.getPromotions({
        searchText: searchText || undefined,
        companyId: Number(selectedInstance),
        customerTypeId: Number(selectedMarket),
        status: selectedStatus || undefined,
      });
      setPromotions(data);
      setHasLoadedPromotions(true);
    } catch (err: any) {
      setFeedback({ message: err.message || t('loadErrorMessage'), severity: 'error' });
      setHasLoadedPromotions(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadPromotions();
  }, [searchText, selectedInstance, selectedMarket, selectedStatus]);

  const handleInstanceChange = (e: SelectChangeEvent) => {
    setSelectedInstance(e.target.value);
    setSelectedMarket('');
    setPromotions([]);
    setHasLoadedPromotions(false);
    setCurrentPage(0);
  };

  const handleMarketChange = (e: SelectChangeEvent) => {
    setSelectedMarket(e.target.value);
    setPromotions([]);
    setHasLoadedPromotions(false);
    setCurrentPage(0);
  };

  const handleSearch = () => {
    setSearchText(searchInput);
    setCurrentPage(0);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchText('');
    setSelectedStatus('');
    setCurrentPage(0);
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    setIsDeleting(true);
    try {
      await promotionsClient.deletePromotion(deleteCandidate.id);
      setFeedback({ message: t('deleteSuccessMessage'), severity: 'success' });
      setDeleteCandidate(null);
      await loadPromotions();
    } catch (err: any) {
      setFeedback({ message: err.message || t('deleteErrorMessage'), severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRowClick = (promo: PromotionDto) => {
    if (canWritePromotions) navigate(`/promotions/edit/${promo.id}`);
  };

  const formatPricingDisplay = (promo: PromotionDto) => {
    if (promo.pricingType === 1) return `${promo.discountPercentage}% off`;
    return `£${promo.fixedPromoUnitPrice?.toFixed(2)}`;
  };

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{ justifyContent: 'space-between', mb: 4 }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('pageSubtitle')}>{t('pageTitle')}</PageTitle>
        </Box>
        {canWritePromotions && (
          <Box width={isMobile ? '100%' : 'auto'}
            sx={{ display: 'flex', justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
            <Button
              variant='primary'
              size='lg'
              startIcon={<SvgIcon component={Plus}
                inheritViewBox />}
              onClick={() => navigate('/promotions/create')}
              sx={{ mb: { xs: 4, md: 0 } }}
            >
              {t('createPromotion')}
            </Button>
          </Box>
        )}
      </Stack>

      {/* Row 1: Search + buttons */}
      <Grid container
        spacing={3}
        sx={{ mb: 2 }}>
        <Grid item
          xs={12}
          sm={6}
          md={5}
          lg={4}>
          <TextField
            fullWidth
            label={t('search')}
            placeholder={t('searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
        </Grid>
        <Grid item
          xs={6}
          sm={3}
          md={2}
          lg={2}>
          <Button fullWidth
            variant='secondary'
            size='lg'
            onClick={handleClear}>
            {t('clear')}
          </Button>
        </Grid>
        <Grid item
          xs={6}
          sm={3}
          md={2}
          lg={2}>
          <Button fullWidth
            variant='primary'
            size='lg'
            onClick={handleSearch}
            startIcon={<SvgIcon><Search /></SvgIcon>}>
            {t('search')}
          </Button>
        </Grid>
      </Grid>

      {/* Row 2: filters */}
      <Grid container
        spacing={3}
        sx={{ mb: 4 }}>
        <Grid item
          xs={12}
          sm={4}>
          <FormControl fullWidth>
            <InputLabel shrink>{t('filterByInstance')}</InputLabel>
            <Select
              value={selectedInstance}
              onChange={handleInstanceChange}
              label={t('filterByInstance')}
              displayEmpty
              notched
            >
              <MenuItem value=''><em>{t('selectInstance')}</em></MenuItem>
              {instances.map((inst) => (
                <MenuItem key={inst.id}
                  value={inst.id.toString()}>{inst.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          xs={12}
          sm={4}>
          <FormControl fullWidth>
            <InputLabel shrink>{t('filterByMarket')}</InputLabel>
            <Select
              value={selectedMarket}
              onChange={handleMarketChange}
              label={t('filterByMarket')}
              displayEmpty
              notched
              disabled={!selectedInstance}
            >
              <MenuItem value=''><em>{t('selectMarket')}</em></MenuItem>
              {markets.map((m) => (
                <MenuItem key={m.id}
                  value={m.id.toString()}>{m.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item
          xs={12}
          sm={4}>
          <FormControl fullWidth>
            <InputLabel shrink>{t('filterByStatus')}</InputLabel>
            <Select
              value={selectedStatus}
              label={t('filterByStatus')}
              displayEmpty
              notched
              onChange={(e: SelectChangeEvent) => {
                setSelectedStatus(e.target.value as StatusFilter);
                setCurrentPage(0);
              }}
            >
              <MenuItem value="">{t('allStatuses')}</MenuItem>
              <MenuItem value="Active">{t('statusActive')}</MenuItem>
              <MenuItem value="Scheduled">{t('statusScheduled')}</MenuItem>
              <MenuItem value="Ended">{t('statusEnded')}</MenuItem>
              <MenuItem value="Deactivated">{t('statusDeactivated')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* Table */}
      <Section>
        {isLoading || !hasLoadedPromotions ? (
          <TableLoading headerHeight={PROMOTIONS_HEADER_HEIGHT}
            bodyRowHeight={PROMOTIONS_ROW_HEIGHT} />
        ) : filteredPromotions.length === 0 ? (
          <EmptyTable
            text={t('pageTitle')}
            searchTerm={searchText}
            onClear={handleClear}
            onAdd={() => navigate('/promotions/create')}
            addItemText={t('createPromotion')}
            onAddIsCreating={false}
            showOnAdd={canWritePromotions}
            headerHeight={PROMOTIONS_HEADER_HEIGHT}
            bodyRowHeight={PROMOTIONS_ROW_HEIGHT}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 2,
                flexDirection: { xs: 'column', md: 'row' },
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant='textMD'
                  fontWeight={600}>{t('pageTitle')}</Typography>
                <Pill text={`${filteredPromotions.length} ${t('pageTitle').toLowerCase()}`} />
              </Box>
            </Box>
            <TableContainer component='div'
              sx={{ overflowX: 'auto' }}>
              <Table size='small'
                aria-label='promotions table'>
                <TableHead sx={{ backgroundColor: theme.palette.custom.gray[100] }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t('name')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('assignedProducts')}</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 600 }}>{t('pricingType')}</TableCell>}
                    <TableCell sx={{ fontWeight: 600 }}>{t('claimed')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('status')}</TableCell>
                    {!isMobile && <TableCell sx={{ fontWeight: 600 }}>{t('endDate')}</TableCell>}
                    {canWritePromotions && <TableCell sx={{ fontWeight: 600 }}>{t('actions')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visiblePromotions.map((promo) => (
                    <TableRow
                      key={promo.id}
                      hover
                      sx={{ cursor: canWritePromotions ? 'pointer' : 'default' }}
                      onClick={() => handleRowClick(promo)}
                    >
                      <TableCell>
                        <Typography variant='textSM'
                          fontWeight={500}>{promo.name}</Typography>
                      </TableCell>
                      <TableCell>{promo.assignedProductCount}</TableCell>
                      {!isMobile && <TableCell>{formatPricingDisplay(promo)}</TableCell>}
                      <TableCell>{promo.claimedQuantity} / {promo.quantityCap}</TableCell>
                      <TableCell>
                        {(() => {
                          const statusLabelMap: Record<PromotionStatusType, string> = {
                            Active: t('statusActive'),
                            Scheduled: t('statusScheduled'),
                            Ended: t('statusEnded'),
                            Deactivated: t('statusDeactivated'),
                          };

                          const statusColorMap: Record<PromotionStatusType, 'success' | 'warning' | 'default'> = {
                            Active: 'success',
                            Scheduled: 'warning',
                            Ended: 'default',
                            Deactivated: 'default',
                          };

                          return (
                            <Chip
                              label={statusLabelMap[promo.status]}
                              color={statusColorMap[promo.status]}
                              size='small'
                            />
                          );
                        })()}
                      </TableCell>
                      {!isMobile && (
                        <TableCell>{new Date(promo.endDateUtc).toLocaleDateString()}</TableCell>
                      )}
                      {canDeletePromotions && (
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <IconButton
                            aria-label={t('delete')}
                            size='small'
                            onClick={() => setDeleteCandidate(promo)}
                          >
                            <SvgIcon style={{ color: theme.palette.error[500] }}><Trash01 /></SvgIcon>
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
              <CustomPagination
                page={currentPage}
                setPage={setCurrentPage}
                maxPages={maxPages}
                breakpointForChangeDisplay={120000}
                useNoDataMessage={false}
                disabled={filteredPromotions.length <= PROMOTIONS_PAGE_SIZE}
              />
            </Box>
          </>
        )}
      </Section>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        open={Boolean(deleteCandidate)}
        icon={<Trash01 />}
        variant='bad'
        title={t('deleteConfirmTitle')}
        message={t('deleteConfirmMessage', { name: deleteCandidate?.name })}
        confirmButtonText={isDeleting ? t('deleting') : t('delete')}
        cancelButtonText={t('cancel')}
        isProcessing={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteCandidate(null)}
      />

      {/* Feedback */}
      {feedback && (
        <MessagesSnackbar
          open={Boolean(feedback)}
          onClose={() => setFeedback(null)}
          message={feedback.message}
          severity={feedback.severity}
          duration={4000}
        />
      )}
    </PageArea>
  );
};

export default ManagePromotions;
